import keycloak from '../config/keycloak';

/**
 * Service to handle Keycloak authentication and token management
 */
class KeycloakService {
  constructor() {
    this.initPromise = null;
    this.initialized = false;
  }

  /**
   * Initialize Keycloak instance
   * @returns {Promise} Promise that resolves when Keycloak is initialized
   */
  init = () => {
    // Return existing promise if already initializing/initialized
    if (this.initPromise) {
      console.log('Keycloak already initializing/initialized, returning existing promise');
      return this.initPromise;
    }

    // Check if keycloak is already initialized
    if (keycloak.authenticated !== undefined || this.initialized) {
      console.log('Keycloak already initialized, returning current state');
      this.initialized = true;
      return Promise.resolve(keycloak.authenticated || false);
    }

    this.initPromise = new Promise((resolve, reject) => {
      console.log('Initializing Keycloak with config:', JSON.stringify(keycloak.config));
      
      keycloak
        .init({
          onLoad: 'check-sso',
          silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
          pkceMethod: 'S256', // PKCE method
          checkLoginIframe: false, // Disable iframe check for better performance
          enableLogging: true, // Enable Keycloak logging
        })
        .then((authenticated) => {
          console.log('Keycloak initialized successfully. Authenticated:', authenticated);
          this.initialized = true;
          if (authenticated) {
            console.log('User is authenticated, setting up token refresh');
            // Setup token refresh
            this.setupTokenRefresh();
          } else {
            console.log('User is not authenticated');
          }
          resolve(authenticated);
        })
        .catch((error) => {
          console.error('Failed to initialize Keycloak:', error);
          // Reset the promise on error so it can be retried
          this.initPromise = null;
          this.initialized = false;
          reject(error);
        });
    });

    return this.initPromise;
  };

  /**
   * Setup automatic token refresh
   */
  setupTokenRefresh = () => {
    // Refresh token before it expires
    setInterval(() => {
      keycloak
        .updateToken(70) // Refresh token if it has less than 70% of its lifetime left
        .then((refreshed) => {
          if (refreshed) {
            console.log('Token refreshed');
          }
        })
        .catch(() => {
          console.error('Failed to refresh token, logging out...');
          this.logout();
        });
    }, 60000); // Check every minute
  };

  /**
   * Login user
   * @param {string} callbackUrl - Optional callback URL to redirect to after login
   * @returns {Promise} Promise that resolves when login is complete
   */
  login = (callbackUrl) => {
    console.log('KeycloakService: Initiating login redirect');
    try {
      // Construct redirect URI with callback URL if provided
      const redirectUri = callbackUrl 
        ? `${window.location.origin}${callbackUrl}`
        : window.location.origin;
      
      console.log('KeycloakService: Redirecting to:', redirectUri);
      return keycloak.login({
        redirectUri: redirectUri,
      });
    } catch (error) {
      console.error('KeycloakService: Error during login redirect:', error);
      throw error;
    }
  };

  /**
   * Logout user
   * @returns {Promise} Promise that resolves when logout is complete
   */
  logout = () => {
    return keycloak.logout({ redirectUri: window.location.origin });
  };

  /**
   * Get user profile information
   * @returns {Promise} Promise that resolves with user profile
   */
  loadUserProfile = () => {
    return new Promise((resolve, reject) => {
      keycloak
        .loadUserProfile()
        .then((profile) => {
          resolve(profile);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  /**
   * Get user roles
   * @returns {Array} Array of user roles
   */
  getRoles = () => {
    return keycloak.realmAccess?.roles || [];
  };

  /**
   * Check if user has a specific role
   * @param {String} role Role to check
   * @returns {Boolean} True if user has the role
   */
  hasRole = (role) => {
    const userRoles = this.getRoles().map(r => r.toLowerCase());
    return userRoles.includes(role.toLowerCase());
  };

  /**
   * Check if user has any of the specified roles
   * @param {Array} roles Array of roles to check
   * @returns {Boolean} True if user has any of the roles
   */
  hasAnyRole = (roles) => {
    console.log('Checking if user has any roles:', roles);
    console.log('User roles:', this.getRoles());
    
    // Ensure roles is an array
    if (!Array.isArray(roles)) {
      console.warn('hasAnyRole expects an array, received:', typeof roles, roles);
      return false;
    }
    
    return roles.some((role) => this.hasRole(role));
  };

  /**
   * Check if user has all of the specified roles
   * @param {Array} roles Array of roles to check
   * @returns {Boolean} True if user has all of the roles
   */
  hasAllRoles = (roles) => {
    console.log('Checking if user has all roles:', roles);
    console.log('User roles:', this.getRoles());
    
    // Ensure roles is an array
    if (!Array.isArray(roles)) {
      console.warn('hasAllRoles expects an array, received:', typeof roles, roles);
      return false;
    }
    
    return roles.every((role) => this.hasRole(role));
  };

  /**
   * Get authentication token
   * @returns {String} Authentication token
   */
  getToken = () => {
    try {
      if (!keycloak.authenticated) {
        console.warn('getToken called but user is not authenticated');
        return null;
      }
      
      if (!keycloak.token) {
        console.warn('User is authenticated but token is missing');
        return null;
      }
      
      console.log('Token retrieved successfully');
      return keycloak.token;
    } catch (error) {
      console.error('Error getting token:', error);
      return null;
    }
  };

  /**
   * Check if user is authenticated
   * @returns {Boolean} True if user is authenticated
   */
  isAuthenticated = () => {
    const authenticated = !!keycloak.authenticated;
    console.log('isAuthenticated check:', authenticated);
    return authenticated;
  };

  /**
   * Get Keycloak instance
   * @returns {Object} Keycloak instance
   */
  getKeycloak = () => {
    return keycloak;
  };
}

export default new KeycloakService();